import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { SvgIconProps } from '@mui/material';
import React from 'react';

import { BoardSeverity } from '../../api/ach/board';

type MuiColor = 'error' | 'warning' | 'info';

interface SeverityMeta {
  label: string;
  color: MuiColor;
  Icon: React.ComponentType<SvgIconProps>;
}

// Presentation for each severity. blocker and critical are both error-colored; the
// icon distinguishes them (blocker is a hard stop). Ordering shown to the user is
// server-defined (severity_rank) — this map is display-only.
export const SEVERITY_META: Record<BoardSeverity, SeverityMeta> = {
  blocker: { label: 'Blocker', color: 'error', Icon: BlockOutlinedIcon },
  critical: { label: 'Critical', color: 'error', Icon: ErrorOutlineIcon },
  warning: { label: 'Warning', color: 'warning', Icon: WarningAmberOutlinedIcon },
  info: { label: 'Info', color: 'info', Icon: InfoOutlinedIcon },
};

export const SEVERITY_ORDER: BoardSeverity[] = ['blocker', 'critical', 'warning', 'info'];

// SeverityIcon renders the icon for a severity in its severity color.
export function SeverityIcon({ severity, fontSize = 'small' }: { severity: BoardSeverity; fontSize?: 'small' | 'medium' }) {
  const meta = SEVERITY_META[severity];
  const { Icon } = meta;
  return <Icon color={meta.color} fontSize={fontSize} aria-label={meta.label} titleAccess={meta.label} />;
}

// formatAge renders a compact age ("3d", "5h", "12m", "just now") from an ISO
// timestamp relative to now. Used for an issue's first_seen.
export function formatAge(iso: string, now: number = Date.now()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) {
    return '';
  }
  const diffMs = Math.max(0, now - then);
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) {
    return 'just now';
  }
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h`;
  }
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

// formatAgo renders a relative "ago" phrase ("2h ago", "just now") from an ISO
// timestamp, reusing formatAge. Used for an issue's last_seen staleness signal.
export function formatAgo(iso: string, now: number = Date.now()): string {
  const age = formatAge(iso, now);
  if (age === '' || age === 'just now') {
    return age;
  }
  return `${age} ago`;
}
